import React, { useRef, useState } from 'react';

import styled from 'styled-components/macro';
import appHelpers from 'utils/helpers/app.helpers';
import { colorPalette } from 'utils/styleConstants';
import Icon from './Icon';

const DropFileContainer = styled.div<{ hasError?: boolean }>`
  position: relative;
  width: 300px;
  height: 70px;
  border: 2px dashed
    ${({ theme, hasError }) => (hasError ? theme.colorPalette.red : theme.colorPalette.turquoise.normal)};
  border-radius: 5px;
  background-color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &.dragover {
    background-color: ${({ theme }) => theme.colorPalette.turquoise.dark};
  }
`;

const DropFileLabel = styled.div`
  text-align: left;
  padding-left: 10px;
`;

const Input = styled.input`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background-color: ${({ theme }) => theme.colorPalette.turquoise.dark};
  cursor: pointer;
`;

const Paragraph = styled.p`
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  font-size: ${({ theme }) => theme.fontSizePalette.small};
  font-weight: ${({ theme }) => theme.fontWeightPalette.medium};
  padding: 10px;
  & strong {
    color: ${({ theme }) => theme.colorPalette.turquoise.normal};
    font-weight: ${({ theme }) => theme.fontWeightPalette.medium};
    text-decoration: underline;
  }
`;

const FileBadge = styled.div`
  box-sizing: border-box;
  height: 49px;
  width: 293px;
  border: 1px solid ${({ theme }) => theme.colorPalette.turquoise.normal};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileName = styled.p`
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: clamp(0.5em, 1em, 16px);
  line-clamp: 1;
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  letter-spacing: -0.5px;
  line-height: 19px;
  text-align: right;
  padding-left: 10px;
`;
const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colorPalette.red};
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  position: absolute;
  font-family: ${({ theme }) => theme.fontFamily};
  bottom: -15px;
  right: 3px;
`;

type Props = {
  onFileChange?: (file: File | null) => void;
  fileTypes?: string[];
  label?: string | JSX.Element;
  hasError?: boolean;
  errorMessage?: string;
};
function DropFileInput(props: Props) {
  const { onFileChange, fileTypes, label, hasError, errorMessage } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [fileList, setFileList] = useState<File | null>(null);

  const onDragEnter = () =>  wrapperRef?.current?.classList.add('dragover');
  const onDragLeave = () =>  wrapperRef?.current?.classList.remove('dragover');
  const onDrop = () => wrapperRef?.current?.classList.remove('dragover');

  const onFileDrop = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files ? e.target.files[0] : null;
    if (newFile && fileTypes && !fileTypes.includes(newFile.type)) {
      appHelpers.notify('fail', 'File type not supported');
      return;
    }
    setFileList(newFile);
    onFileChange?.(newFile);
  };

  const fileRemove = () => {
    setFileList(null);
    onFileChange?.(null);
  };

  return (
    <>
      {!fileList ? (
        <DropFileContainer
          ref={wrapperRef}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          hasError={hasError}
        >
          <Icon
            style={{
              color: `${colorPalette.turquoise.normal}`,
              cursor: 'pointer',
            }}
            size={'45px'}
            icon="cloud-upload-alt"
          />
          <DropFileLabel>
            <Paragraph>{label}</Paragraph>
          </DropFileLabel>
          <Input type="file" value="" name="" onChange={onFileDrop} accept={fileTypes?.join(' ')} />
          {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </DropFileContainer>
      ) : (
        <FileBadge>
          <Icon
            icon="file-pdf"
            style={{
              display: 'inline',
              color: `${colorPalette.turquoise.normal}`,
            }}
            size={'25px'}
          />
          <FileName>{fileList.name.length <= 27 ? fileList.name : fileList.name.slice(0, 24).concat('...')}</FileName>
          <Icon
            icon="times"
            onClick={fileRemove}
            style={{
              display: 'inline',
              color: `${colorPalette.turquoise.normal}`,
              width: '18px',
              height: '23px',
              margin: '0 10px',
              cursor: 'pointer',
            }}
          />
        </FileBadge>
      )}
    </>
  );
}
export default DropFileInput;
